import { useMemo, useCallback } from "react";

import { DataFile, DataFilter, ModelInterface } from "interfaces";
import { FlatDistribution, FlatBiVariateDistribution } from "interfaces";

import {
  useGetDataInsight,
  useGetMultipleInsights,
  useGetInsightExport,
  insightUrls,
} from "./insights";
import { useGetColumnType } from "./column-types";

export const useFetchDataRows = (
  dataFileId: string,
  columns: string[],
  filters?: DataFilter.Filter[]
) =>
  useGetDataInsight<{ [col: string]: (string | number | Date)[] }>({
    fileId: dataFileId,
    name: "data_rows",
    params: { columns, filters: filters || [] },
  });

export const useFetchOutlierRows = (
  dataFileId: string,
  column: string,
  colType: DataFile.ColType,
  filters?: DataFilter.Filter[]
) => {
  return useGetDataInsight<{ [col: string]: (string | number | Date)[] }>({
    fileId: dataFileId,
    name: "data_column_outlier_rows",
    params: { column, col_type: colType, filters },
  });
};

export const useFetchOutlierRowsForColumns = (
  dataFileId: string,
  columns: string[],
  colTypes: DataFile.UserDefinedTypes,
  filters?: DataFilter.Filter[]
) => {
  const specs = useMemo(
    () =>
      columns.map((c) => ({
        column: c,
        col_type: colTypes?.[c],
        filters: filters || [],
      })),
    [columns, colTypes, filters]
  );

  return useGetMultipleInsights<{ [col: string]: (string | number | Date)[] }>(
    dataFileId,
    "data_column_outlier_rows",
    specs
  );
};

export const useFetchColumnProfile = (
  dataFileId: string,
  column: string,
  colType: DataFile.ColType,
  filters?: DataFilter.Filter[]
) => {
  const spec =
    Array.isArray(filters) && filters.length >= 1
      ? {
          fileId: dataFileId,
          name: "data_filtered_column_details" as keyof typeof insightUrls,
          params: { column, filters, col_type: colType },
        }
      : {
          fileId: dataFileId,
          name: "data_column_profile" as keyof typeof insightUrls,
          params: { column, col_type: colType },
        };

  return useGetDataInsight<DataFile.ColumnProfile>(spec);
};

export const useFetchMultipleColumnProfiles = (
  dataFileId: string,
  columns: string[],
  colTypes: DataFile.UserDefinedTypes,
  filters?: DataFilter.Filter[]
) => {
  const result = useGetDataInsight<
    {
      column: string;
      data: DataFile.ColumnProfile;
    }[]
  >({
    fileId: dataFileId,
    name: "data_column_profiles",
    params: { columns, col_types: colTypes, filters: filters || [] },
  });
  if (!result.data) {
    return columns.map(
      (c) =>
        ({ ...result, data: undefined } as ReturnType<
          typeof useFetchColumnProfile
        >)
    );
  }
  return columns.map(
    (c) =>
      ({
        ...result,
        data: result.data.find((d) => d.column === c)?.data,
      } as ReturnType<typeof useFetchColumnProfile>)
  );
};

export const useFetchColumnGroupedDistributions = (
  dataFileId: string,
  columns: string[],
  colTypes: DataFile.UserDefinedTypes,
  filters?: DataFilter.Filter[],
  groupingVariables?: string[],
  enabled = true
) => {
  const specs = columns.map((c) => ({
    value_column: c,
    col_type: colTypes?.[c] || "numeric",
    filters,
    grouping_columns: groupingVariables,
  }));

  return useGetMultipleInsights<DataFile.GroupedDistributions[]>(
    dataFileId,
    "data_grouped_distributions",
    specs,
    enabled
  );
};

export const useFetchPivot = (
  fileId: string,
  grouping_columns: string[],
  value_column: string,
  filters?: DataFilter.Filter[],
  sort_by?: string[],
  sort_asc?: boolean,
  limit = 1000,
  offset = 0,
  enabled = true
) => {
  const col_type = useGetColumnType(fileId, value_column);
  const params = {
    grouping_columns,
    value_column,
    col_type,
    filters: filters || [],
    sort_by,
    sort_asc,
    limit,
    offset,
  };
  return useGetDataInsight<{
    data: DataFile.GroupedDistributions[];
    size: number;
    limit: number;
    offset: number;
  }>({ fileId, name: "data_pivot", params, enabled });
};

export const useExportPivot = (
  fileId: string,
  grouping_columns: string[],
  value_column: string,
  filters?: DataFilter.Filter[],
  outputFileType?: "csv" | "pq"
) => {
  const { caller, status, error } = useGetInsightExport(
    fileId,
    "data_pivot_export"
  );

  const col_type = useGetColumnType(fileId, value_column);

  const exportGetter = useCallback(async () => {
    if (status === "in-progress" || status === "completed") {
      return;
    }
    const params = {
      grouping_columns,
      value_column,
      col_type,
      filters: filters || [],
      export_format: outputFileType || "csv",
    };
    await caller(params);
  }, [
    caller,
    status,
    grouping_columns,
    value_column,
    col_type,
    filters,
    outputFileType,
  ]);

  return { exportGetter, status, error };
};

export const useFetchColumnComparisons = (
  dataFileId: string,
  column1: string,
  column2: string,
  column1Type?: "numeric" | "char" | "date",
  column2Type?: "numeric" | "char" | "date",
  filters?: DataFilter.Filter[]
) => {
  return useGetDataInsight<DataFile.ColumnComparison>({
    fileId: dataFileId,
    name: "data_compare_columns",
    params: {
      column1,
      column2,
      column1_type: column1Type,
      column2_type: column2Type,
      filters: filters,
    },
  });
};

export const useFetchColumnComparisonsBetweenSets = (
  dataFileId: string,
  columnSet1: string[],
  columnSet2: string[],
  colTypes: DataFile.UserDefinedTypes,
  filters?: DataFilter.Filter[]
) => {
  useFetchMultipleColumnProfiles(
    dataFileId,
    Array.from(new Set([...columnSet1, ...columnSet2])),
    colTypes,
    filters
  );
  return useGetDataInsight<
    { pair: [string, string]; data: DataFile.ColumnComparison }[]
  >({
    fileId: dataFileId,
    name: "data_compare_column_sets",
    params: {
      columns1: columnSet1,
      columns2: columnSet2,
      col_types: colTypes,
      filters: filters || [],
    },
    enabled: !!dataFileId && columnSet2.length >= 1 && columnSet2.length >= 1,
  });
};

export const useFetchYImpactOnColumns = (
  dataFileId: string,
  valueColumns: string[],
  yColumn: string,
  colTypes: DataFile.UserDefinedTypes,
  filters?: DataFilter.Filter[],
  groupingVariables?: string[],
  maxClusters = 16
) => {
  return useGetDataInsight<
    DataFile.ContinousYImpact[] | DataFile.DiscreteYImpact[]
  >({
    fileId: dataFileId,
    name: "data_y_impact",
    params: {
      value_columns: valueColumns,
      y_column: yColumn,
      column_types: colTypes,
      filters,
      grouping_columns: groupingVariables,
      max_cluster_count: maxClusters,
    },
  });
};

export const useFetchMultipleYImpactClusters = (
  dataFileId: string,
  valueColumns: string[],
  yColumns: string[],
  colTypes: DataFile.UserDefinedTypes,
  filters?: DataFilter.Filter[],
  groupingVariables?: string[],
  maxClusters = 16
) => {
  const spec = {
    value_columns: valueColumns,
    y_columns: yColumns,
    column_types: colTypes,
    filters,
    grouping_columns: groupingVariables,
    max_cluster_count: maxClusters,
  };

  return useGetDataInsight<{
    [y: string]: DataFile.ContinousYImpact[] | DataFile.DiscreteYImpact[];
  }>({
    fileId: dataFileId,
    name: "data_y_impact",
    params: spec,
    enabled: !!dataFileId && (valueColumns || []).length >= 1,
  });
};

export const useFetchYImpactForFile = (
  dataFileId: string,
  yColumn: string,
  colTypes: DataFile.UserDefinedTypes,
  filters?: DataFilter.Filter[],
  groupingVariables?: string[],
  colsToExclude?: string[]
) => {
  return useGetDataInsight<{
    [col: string]: DataFile.ContinousYImpact[] | DataFile.DiscreteYImpact[];
  }>({
    fileId: dataFileId,
    name: "file_univariate_impact",
    params: {
      y_column: yColumn,
      column_types: colTypes,
      filters,
      grouping_columns: groupingVariables,
      cols_to_exclude: colsToExclude,
    },
    enabled: !!dataFileId,
  });
};

export const useFetchMultipleYImpactForFile = (
  dataFileId: string,
  yColumns: string[],
  colTypes: DataFile.UserDefinedTypes,
  filters?: DataFilter.Filter[],
  groupingVariables?: string[],
  colsToExclude?: string[]
) => {
  const spec = {
    y_columns: yColumns,
    column_types: colTypes,
    filters,
    grouping_columns: groupingVariables,
    cols_to_exclude: colsToExclude,
  };

  const resp = useGetDataInsight<{
    [y: string]: {
      [col: string]: DataFile.ContinousYImpact[] | DataFile.DiscreteYImpact[];
    };
  }>({
    fileId: dataFileId,
    name: "file_univariate_impact",
    params: spec,
    enabled: !!dataFileId && (yColumns || []).length >= 1,
  });

  const ret = yColumns.map((y) => ({ ...resp, data: resp.data?.[y] }));

  return ret;

  // const specs = yColumns.map((yColumn) => ({
  //   y_column: yColumn,
  //   column_types: colTypes,
  //   filters,
  //   grouping_columns: groupingVariables,
  //   cols_to_exclude: colsToExclude,
  // }));

  // return useGetMultipleInsights<{
  //   [col: string]: DataFile.ContinousYImpact[] | DataFile.DiscreteYImpact[];
  // }>(
  //   dataFileId,
  //   "file_univariate_impact",
  //   specs,
  //   yColumns.length >= 1 && Object.keys(colTypes).length >= 1
  // );
};

export const useFetchTriVariateDistribution = (
  dataFileId: string,
  column1: string,
  column2: string,
  valueColumn: string | undefined,
  colTypes: DataFile.UserDefinedTypes,
  filters?: DataFilter.Filter[],
  groupingVariables?: string[]
) => {
  return useGetDataInsight<FlatBiVariateDistribution>({
    fileId: dataFileId,
    name: "tri_variate_distributions",
    params: {
      column1,
      column2,
      value_column: valueColumn,
      column1_type: colTypes[column1],
      column2_type: colTypes[column2],
      filters,
      grouping_columns: groupingVariables,
    },
  });
};

export const useFetchTriVariateDistributions = (
  dataFileId: string,
  column1: string,
  column2: string,
  valueColumns: string[],
  colTypes: DataFile.UserDefinedTypes,
  filters?: DataFilter.Filter[],
  groupingVariables?: string[]
) => {
  const specs = valueColumns.map((vc) => ({
    column1,
    column2,
    value_column: vc,
    column1_type: colTypes[column1],
    column2_type: colTypes[column2],
    filters,
    grouping_columns: groupingVariables,
  }));
  return useGetMultipleInsights<FlatBiVariateDistribution>(
    dataFileId,
    "tri_variate_distributions",
    specs,
    column1 && column2 && column2 !== column1 && valueColumns.length >= 1
  );
};

export const useFetchBivariateDistributions = (
  dataFileId: string,
  columnSet1: string[],
  columnSet2: string[],
  valueColumns: string[],
  colTypes: DataFile.UserDefinedTypes,
  filters?: DataFilter.Filter[],
  groupingVariables?: string[]
) => {
  return useGetDataInsight<
    {
      column1: string;
      column2: string;
      value_column: string | null | undefined;
      data: FlatBiVariateDistribution;
    }[]
  >({
    fileId: dataFileId,
    name: "data_bivariate_distributions",
    params: {
      column_set_1: columnSet1,
      column_set_2: columnSet2,
      value_columns: valueColumns,
      col_types: colTypes,
      grouping_columns: groupingVariables,
      filters: filters,
    },
    enabled: !!dataFileId && columnSet1.length >= 1 && columnSet2.length >= 1,
  });
};

export const useFetchRelationships = (
  fileId: string,
  filters?: DataFilter.Filter[],
  groupingVariables?: string[]
) => {
  return useGetDataInsight<
    {
      grouping: string | string[] | null;
      relationships: {
        [column: string]: DataFile.Relationships;
      };
      data_count: number;
    }[]
  >({
    fileId,
    name: "data_relationship_matrix",
    params: { filters, grouping_variables: groupingVariables },
  });
};

export const useFetchCrossRelationships = (
  fileId: string,
  columns1: string[],
  columns2: string[],
  colTypes?: DataFile.UserDefinedTypes,
  filters?: DataFilter.Filter[],
  groupingVariables?: string[]
) =>
  useGetDataInsight<
    {
      grouping: string | string[] | null;
      relationships: {
        [column: string]: DataFile.Relationships;
      };
      data_count: number;
    }[]
  >({
    fileId,
    name: "data_cross_relationships",
    params: {
      columns1,
      columns2,
      column_types: colTypes,
      filters,
      grouping_variables: groupingVariables,
    },
    enabled: columns1.length >= 1 && columns2.length >= 1,
  });

export const useFetchGroupedColumnCounts = (
  fileId: string,
  groupingVariables: string[],
  valueVariables: string[],
  colTypes?: DataFile.UserDefinedTypes,
  filters?: DataFilter.Filter[]
) =>
  useGetDataInsight<{
    [valueColumn: string]: {
      group: string[];
      count: number;
    }[];
  }>({
    fileId,
    name: "data_column_valid_counts",
    params: {
      value_columns: valueVariables,
      grouping_columns: groupingVariables,
      column_types: colTypes,
      filters,
    },
    enabled: valueVariables.length >= 1 && groupingVariables.length >= 1,
  });

export const useFetchColumnCounts = (
  fileId: string,
  valueVariables: string[],
  colTypes?: DataFile.UserDefinedTypes,
  filters?: DataFilter.Filter[]
) =>
  useGetDataInsight<{
    [value_column: string]: number;
  }>({
    fileId,
    name: "data_column_valid_counts",
    params: {
      value_columns: valueVariables,
      column_types: colTypes,
      filters,
    },
    enabled: valueVariables.length >= 1,
  });

export const useFetchColumnJointCounts = (
  fileId: string,
  columns: string[],
  filters?: DataFilter.Filter[]
) =>
  useGetDataInsight<
    {
      group: string[];
      count: number;
    }[]
  >({
    fileId,
    name: "data_joint_counts",
    params: {
      columns,
      filters,
    },
    enabled: columns.length >= 1,
  });

interface GroupDifferencesResult {
  [column: string]: {
    [key: string]: {
      test: string;
      stat: string;
      value: number;
      "p-value": number;
      decision:
        | "Too little data"
        | "Not Significant"
        | "Slightly Significant"
        | "Highly significant"
        | "Very highly significant";
      sizes: number[];
    };
  };
}

export const useGetGroupDifferencesSummary = (
  fileId: string,
  groupDefinitions: DataFilter.Filter[][],
  ignoreUngroupedData: boolean
) => {
  return useGetDataInsight<GroupDifferencesResult>({
    fileId,
    name: "data_group_differences_summary",
    params: {
      group_definitions: groupDefinitions,
      ignore_ungrouped_data: ignoreUngroupedData,
    },
    enabled:
      Array.isArray(groupDefinitions) &&
      groupDefinitions.length >= (ignoreUngroupedData ? 2 : 1),
  });
};

export const useGetGroupDifferencesColumnDetails = (
  fileId: string,
  groupDefinitions: DataFilter.Filter[][],
  ignoreUngroupedData: boolean,
  column: string
) => {
  return useGetDataInsight<FlatDistribution[]>({
    fileId,
    name: "data_group_differences_column_details",
    params: {
      column,
      group_definitions: groupDefinitions,
      ignore_ungrouped_data: ignoreUngroupedData,
    },
    enabled:
      !!column &&
      Array.isArray(groupDefinitions) &&
      groupDefinitions.length >= (ignoreUngroupedData ? 2 : 1),
  });
};

interface ModelResp {
  algorithm: string;
  diagnostics: {
    r2?: number;
    "adjusted-r2"?: number;
    mae?: number;
    mape?: number;
    mse?: number;
    rmse?: number;
    "corrected-bic"?: number;
    "cv-mse"?: number[];
    "cv-rmse"?: number;
  };
  hyper_params: { [key: string]: string | number | (string | number)[] };
  impacts: {
    [column: string]: {
      abs: {
        count: number;
        mean: number;
        std: number;
        min: number;
        "25%": number;
        "50%": number;
        "75%": number;
        max: number;
      };
      raw: {
        count: number;
        mean: number;
        std: number;
        min: number;
        "25%": number;
        "50%": number;
        "75%": number;
        max: number;
      };
    };
  };
  model: ModelInterface;
  model_candidates: string[];
  "y-stats": {
    count: number;
    mean: number;
    median: number;
    "unique-count": number;
  };
}

export const useFetchYModel = (
  fileId: string,
  yColumn: string,
  candidates: string[],
  colTypes: DataFile.UserDefinedTypes,
  algorithm: string,
  hyper_params?: { [key: string]: number | string | (number | string)[] },
  groupingColumns?: string[],
  colsForDiscretization?: string[],
  n_bins?: number,
  filters?: DataFilter.Filter[],
  dropRowsWithMissingValues?: boolean
) => {
  return useGetDataInsight<ModelResp>({
    fileId,
    name: "data_y_model",
    params: {
      y_column: yColumn,
      candidates: candidates,
      column_types: colTypes,
      algorithm: algorithm,
      hyper_params: hyper_params || {},
      grouping_columns: groupingColumns || [],
      filters: filters || [],
      cols_to_discretize: colsForDiscretization || [],
      n_bins: n_bins,
      drop_rows_with_missing_values: dropRowsWithMissingValues,
    },
    enabled: !!yColumn && Array.isArray(candidates) && candidates.length >= 1,
  });
};

export const useFetchModelsForMultipleYs = (
  fileId: string,
  yColumns: string[],
  candidates: string[],
  colTypes: DataFile.UserDefinedTypes,
  algorithm: string,
  hyper_params?: { [key: string]: number | string | (number | string)[] },
  groupingColumns?: string[],
  colsForDiscretization?: string[],
  n_bins?: number,
  filters?: DataFilter.Filter[],
  dropRowsWithMissingValues?: boolean
) => {
  const spec = {
    y_columns: yColumns,
    candidates: candidates,
    column_types: colTypes,
    algorithm: algorithm,
    hyper_params: hyper_params || {},
    grouping_columns: groupingColumns || [],
    filters: filters || [],
    cols_to_discretize: colsForDiscretization || [],
    n_bins: n_bins,
    drop_rows_with_missing_values: dropRowsWithMissingValues,
  };

  const resp = useGetDataInsight<{ [y: string]: ModelResp }>({
    fileId,
    name: "data_y_model",
    params: spec,
    enabled:
      yColumns.length >= 1 &&
      Array.isArray(candidates) &&
      candidates.length >= 1,
  });

  return yColumns.map((y) => ({ ...resp, data: resp.data?.[y] }));
};

export const useGetGroupDiscriminators = (
  fileId: string,
  groupDefinitions: { name: string; definition: DataFilter.Filter[] }[],
  ignoreUngroupedData: boolean,
  candidates: string[],
  colTypes: DataFile.UserDefinedTypes,
  maxClusterCount: number
) => {
  return useGetDataInsight<{
    discriminators: DataFile.DiscreteYImpact;
    "group-stats": { [level: string]: number };
  }>({
    fileId,
    name: "data_group_discriminators",
    params: {
      group_definitions: groupDefinitions,
      ignore_ungrouped_data: ignoreUngroupedData,
      candidate_columns: candidates,
      column_types: colTypes,
      max_cluster_count: maxClusterCount,
    },
    enabled:
      Array.isArray(groupDefinitions) &&
      groupDefinitions.length >= (ignoreUngroupedData ? 2 : 1) &&
      (candidates || []).length >= 1,
  });
};
